import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  KakaoLoginRequest,
  KakaoRegistrationRequest,
  UpdateUserRequest,
  User,
  GetCupidCardsResponse,
  OpenCardRequest,
  ExtendCardRequest,
  CupidCard,
  CreateChatRequest,
  Chat,
  GetChatMessagesRequest,
  SendMessageRequest,
  Message,
  UploadImageResponse,
  GetUserStatsResponse,
} from '../types';

const API_BASE_URL = 'https://api.randomday.net';

class ApiClient {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error || 'API 요청이 실패했습니다.');
    }

    return data;
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    if (response.data) {
      localStorage.setItem('accessToken', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }
    
    return response.data!;
  }

  async kakaoLogin(code: string, redirectUri: string): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/auth/kakao/login', {
      method: 'POST',
      body: JSON.stringify({ code, redirectUri }),
    });
    
    if (response.data) {
      localStorage.setItem('accessToken', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }
    
    return response.data!;
  }

  async register(userData: RegisterRequest): Promise<User> {
    const response = await this.request<User>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    return response.data!;
  }

  async completeKakaoRegistration(userData: KakaoRegistrationRequest): Promise<User> {
    const response = await this.request<User>('/auth/complete-registration', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    return response.data!;
  }

  async logout(): Promise<void> {
    await this.request('/auth/logout', {
      method: 'POST',
    });
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('리프레시 토큰이 없습니다.');
    }

    const response = await this.request<{ token: string; refreshToken: string }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });

    if (response.data) {
      localStorage.setItem('accessToken', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      return response.data.token;
    }

    throw new Error('토큰 갱신에 실패했습니다.');
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.request<User>('/users/me');
    return response.data!;
  }

  async updateUser(updates: UpdateUserRequest): Promise<User> {
    const response = await this.request<User>('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    
    return response.data!;
  }

  async getUserStats(): Promise<GetUserStatsResponse> {
    const response = await this.request<GetUserStatsResponse>('/users/stats');
    return response.data!;
  }

  async getCupidCards(page: number = 1, limit: number = 10): Promise<GetCupidCardsResponse> {
    const response = await this.request<GetCupidCardsResponse>(`/cards?page=${page}&limit=${limit}`);
    return response.data!;
  }

  async openCard(request: OpenCardRequest): Promise<CupidCard> {
    const response = await this.request<CupidCard>('/cards/open', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    
    return response.data!;
  }

  async extendCard(request: ExtendCardRequest): Promise<CupidCard> {
    const response = await this.request<CupidCard>('/cards/extend', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    
    return response.data!;
  }

  async createChat(request: CreateChatRequest): Promise<Chat> {
    const response = await this.request<Chat>('/chats', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    
    return response.data!;
  }

  async getChats(): Promise<Chat[]> {
    const response = await this.request<Chat[]>('/chats');
    return response.data!;
  }

  async getChatMessages(request: GetChatMessagesRequest): Promise<Message[]> {
    const { chatId, page = 1, limit = 50 } = request;
    const response = await this.request<Message[]>(`/chats/${chatId}/messages?page=${page}&limit=${limit}`);
    return response.data!;
  }

  async sendMessage(request: SendMessageRequest): Promise<Message> {
    const response = await this.request<Message>(`/chats/${request.chatId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content: request.content }),
    });
    
    return response.data!;
  }

  async uploadImage(file: File): Promise<UploadImageResponse> {
    const formData = new FormData();
    formData.append('image', file);

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/upload/image`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('이미지 업로드에 실패했습니다.');
    }

    const data = await response.json();
    return data;
  }

  async deleteAccount(): Promise<void> {
    await this.request('/users/me', {
      method: 'DELETE',
    });

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

}

export const apiClient = new ApiClient();